<script>
  import { onMount, tick } from 'svelte'
  import {
    getAccountBalance,
    addGiftCardToAccount,
    lookupGiftCard,
    redeemGift,
  } from '../../models/giftCard.js'
  import { formatPrice, formatDate } from '../../services/utilities.js'
  import { auth } from '../../services/auth.js'
  import { stripe } from '../../services/stripe.js'
  import Alert from '../../components/Alert.svelte'
  import GiftPageHeader from '../../components/GiftPageHeader.svelte'

  const returnTo = '/redeem'
  // remembers a code typed in while signed out so we can finish after sign in
  const pendingKey = 'pendingGiftCode'

  let user = auth.getAuthenticatedUser()
  let balance = 0
  let code = ''
  let working = false
  // { type: 'danger' | 'info', text } shown right under the code box;
  // info is for things that aren't the customer's mistake
  let notice = null

  // result of looking up the code:
  // 'card-added' | 'card-needs-account' | 'needs-account' | 'membership'
  let result = null
  let addedAmount = 0
  let cardAmount = 0

  // gifted membership (codes bought before membership gifting was retired)
  let gifted = null
  let termsAcked = false
  let payAcked = false
  let starting = false

  let choice = null
  const choices = [
    { id: 'membership', label: 'Start a membership' },
    { id: 'pack', label: 'Rent a Puzzle Pack' },
    { id: 'buy', label: 'Buy puzzles to keep' },
  ]

  onMount(async () => {
    if (!user) return
    await refreshBalance()

    let pending = null
    try {
      pending = sessionStorage.getItem(pendingKey)
    } catch (e) {}
    if (pending) {
      code = pending
      await redeem()
      // keep it for another try if something went wrong on our side
      if (!(notice && notice.text === problemMsg)) forgetCode()
    }
  })

  // the balance is only extra information, so a failed fetch is ignored
  async function refreshBalance() {
    try {
      let res = await getAccountBalance(false)
      if (!res.error) {
        balance = res.balance
      }
    } catch (e) {}
  }

  function cleanCode(value) {
    // codes are emailed as lowercase uuids; forgive pasted spaces and caps
    return (value || '').replace(/\s+/g, '').toLowerCase()
  }

  const notFoundMsg =
    "We couldn't find that code. Please check that it matches the code in your email exactly, then try again."
  const problemMsg =
    'Something went wrong while checking your code. Please try again, or email info@puzzleexchange.com and we will help.'

  const danger = (text) => ({ type: 'danger', text })
  const info = (text) => ({ type: 'info', text })

  // the redeem endpoint answers 404 for an unknown code, and 200 with an error
  // when the code exists but a rule blocks it (already redeemed, already a
  // subscriber, unpaid order). Those aren't typing mistakes, so they're info.
  function membershipNotice({ status, data }) {
    if (status === 404) return danger(notFoundMsg)
    if (status === 200 && data.error) return info(data.error)
    return danger(problemMsg)
  }

  function usedCardNotice() {
    if (user && balance > 0) {
      return info(
        `This gift card has already been redeemed. You have ${formatPrice(balance)} in gift balance on your account, ready to use.`,
      )
    }
    return info(
      "This gift card has already been redeemed, so there's no money left on it. If you think this is a mistake, email info@puzzleexchange.com and we will help.",
    )
  }

  async function startOver() {
    code = ''
    notice = null
    result = null
    gifted = null
    termsAcked = false
    payAcked = false
    await tick()
    document.getElementById('giftCode')?.focus()
  }

  function rememberCode(value) {
    try {
      sessionStorage.setItem(pendingKey, value)
    } catch (e) {}
  }

  function forgetCode() {
    try {
      sessionStorage.removeItem(pendingKey)
    } catch (e) {}
  }

  async function redeem() {
    notice = null
    result = null
    gifted = null
    let c = cleanCode(code)
    code = c

    if (!c) {
      notice = danger('Please type or paste the gift code from your email.')
      return
    }

    working = true
    try {
      // gift card lookup works without an account, so try it first
      let lookup = await lookupGiftCard(c)
      if (lookup.status !== 200 && lookup.status !== 404) {
        notice = danger(problemMsg)
        return
      }
      let gc = lookup.data
      if (lookup.status === 200) {
        if (gc.remaining_balance === 0) {
          notice = usedCardNotice()
        } else if (!user) {
          cardAmount = gc.remaining_balance
          rememberCode(c)
          result = 'card-needs-account'
        } else {
          let res = await addGiftCardToAccount(c)
          if (res.error) {
            // it may have been redeemed since the lookup; otherwise it's on us
            let again = await lookupGiftCard(c)
            notice =
              again.status === 200 && again.data.remaining_balance === 0
                ? usedCardNotice()
                : danger(problemMsg)
          } else {
            addedAmount = res.amount
            await refreshBalance()
            result = 'card-added'
          }
        }
        return
      }

      // not a gift card, so it may be a gifted membership code
      if (!user) {
        rememberCode(c)
        result = 'needs-account'
        return
      }

      let res = await redeemGift(c, false)
      if (res.status === 200 && !res.data.error) {
        gifted = res.data
        result = 'membership'
      } else {
        notice = membershipNotice(res)
      }
    } catch (e) {
      notice = danger(problemMsg)
    } finally {
      working = false
    }
  }

  async function activateMembership() {
    notice = null
    starting = true
    try {
      let res = await redeemGift(code, true)
      if (res.status !== 200 || res.data.error || !res.data.stripe_session_id) {
        notice = membershipNotice(res)
        starting = false
        return
      }
      stripe.storeSession(res.data.stripe_session_id)
      await stripe.redirect(res.data.stripe_session_id)
    } catch (err) {
      notice = danger(problemMsg)
      starting = false
    }
  }
</script>

<svelte:head>
  <title>Redeem Your Gift | Missing Piece Puzzle Exchange</title>
</svelte:head>

<style type="text/less">
  .redeem-page {
    max-width: 820px;
    margin: 0 auto;
    padding: calc(var(--spacing) * 2) var(--spacing) var(--spacing);
    font-size: 1.125rem;
    line-height: 1.6;
    color: var(--col-gray-10);

    p {
      margin: 0 0 var(--spacing-sm);
    }
  }

  .step {
    background: var(--col-gray-100);
    border: 1px solid var(--col-gray-90);
    border-radius: 16px;
    padding: var(--spacing-lg);
    margin-bottom: var(--spacing-lg);

    h2 {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      margin: 0 0 var(--spacing-sm);
    }
  }

  .step-num {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: var(--col-accent-2);
    color: var(--col-gray-100);
    font-size: 1.25rem;
    font-weight: 700;
  }

  .code-label {
    display: block;
    font-weight: 700;
    margin-bottom: var(--spacing-xs);
  }

  .code-row {
    display: flex;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
    margin-bottom: var(--spacing-xs);
  }

  .code-input {
    flex: 1 1 320px;
    min-height: 3.25rem;
    font-size: 1.125rem;
    padding: 0 var(--spacing-sm);
    border: 2px solid var(--col-gray-60);
    border-radius: 6px;

    &:focus {
      border-color: var(--col-accent-2);
      outline: 2px solid var(--col-accent-1-xl);
    }
  }

  .big-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-xs);
    min-height: 3.25rem;
    font-size: 1.125rem;
    line-height: 1.3;
    padding: var(--spacing-xs) var(--spacing-lg);
    text-align: center;
  }

  .hint {
    color: var(--col-gray-30);
    font-size: 1rem;
  }

  .existing-balance {
    margin-top: var(--spacing-sm);
  }

  .notice {
    margin: var(--spacing-sm) 0;
  }

  .start-over {
    margin-top: var(--spacing-sm);
    font-size: 1.125rem;
  }

  .result {
    border-radius: 8px;
    padding: var(--spacing);
    margin-top: var(--spacing);

    &.success {
      background: var(--col-success-xl);
      border: 2px solid var(--col-success-d);
    }

    &.info {
      background: var(--col-accent-1-xl);
      border: 2px solid var(--col-accent-2-l);
    }

    h3 {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      margin: 0 0 var(--spacing-xs);

      .icon {
        width: 1.75rem;
        height: 1.75rem;
      }
    }
  }

  .auth-buttons {
    display: flex;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
    margin-top: var(--spacing-sm);

    .btn {
      flex: 1 1 220px;
      margin: 0;
      text-align: center;
    }
  }

  .terms {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    margin: var(--spacing-sm) 0;

    label {
      display: flex;
      gap: var(--spacing-sm);
      align-items: flex-start;
    }

    input {
      flex: 0 0 auto;
      width: 1.5rem;
      height: 1.5rem;
      margin-top: 0.15rem;
    }
  }

  .choices {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    margin: var(--spacing-sm) 0;
  }

  .choice {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    width: 100%;
    min-height: 3.5rem;
    padding: 0 var(--spacing);
    font-size: 1.125rem;
    font-weight: 600;
    text-align: left;
    background: var(--col-gray-100);
    color: var(--col-gray-10);
    border: 2px solid var(--col-gray-70);
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      border-color: var(--col-accent-2);
    }

    &.selected {
      border-color: var(--col-accent-2);
      background: var(--col-accent-1-xl);
    }

    .radio {
      flex: 0 0 auto;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 50%;
      border: 2px solid var(--col-accent-2);
      box-shadow: inset 0 0 0 3px var(--col-gray-100);
    }

    &.selected .radio {
      background: var(--col-accent-2);
    }

    .chevron {
      margin-left: auto;
      width: 1.75rem;
      height: 1.75rem;
      transition: transform 0.2s;
    }

    &.selected {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      .chevron {
        transform: rotate(180deg);
      }
    }
  }

  .details {
    border: 2px solid var(--col-accent-2);
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: var(--spacing);
    margin-top: calc(var(--spacing-sm) * -1);
    background: var(--col-gray-100);

    .btn {
      width: 100%;
    }

    ol {
      padding-left: 1.5rem;
      margin: 0 0 var(--spacing-sm);

      li {
        margin-bottom: var(--spacing-xs);
      }
    }

    .important {
      font-weight: 600;
    }
  }

  .help {
    text-align: center;
    margin-top: var(--spacing-lg);
  }

  @media (max-width: 600px) {
    // give the content as much of a small screen as possible
    .redeem-page {
      padding: var(--spacing) var(--spacing-sm);
    }

    .step {
      padding: var(--spacing) var(--spacing-sm);
    }

    .choice {
      padding: 0 var(--spacing-sm);
    }

    .details {
      padding: var(--spacing-sm);
    }

    .big-btn {
      width: 100%;
    }
  }
</style>

<GiftPageHeader
  title="Redeem Your Gift"
  intro="Someone gave you the gift of puzzling! Enter the code from your gift email below and we'll walk you through the rest." />

<section class="redeem-page">

  <div class="step">
    <h2><span class="step-num">1</span> Enter your gift code</h2>

    {#if !result}
      <form on:submit|preventDefault={() => redeem()}>
        <label class="code-label" for="giftCode">Gift code</label>
        <div class="code-row">
          <input
            class="code-input"
            id="giftCode"
            type="text"
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Paste or type your code here"
            bind:value={code}
            on:input={() => (notice = null)} />
          <button class="btn big-btn" type="submit" disabled={working}>
            {#if working}
              <svg class="icon spin"><use href="/icons.svg#circle_notch" /></svg>
              Checking...
            {:else}Redeem my gift{/if}
          </button>
        </div>
        {#if notice}
          <div class="notice">
            <Alert
              type={notice.type === 'info' ? 'accent-2' : 'danger'}
              icon={notice.type === 'info' ? 'info' : 'warning'}>
              {notice.text}
            </Alert>
          </div>
        {/if}
        {#if !notice}
          <p class="hint">
            You'll find the code in the email you received. Copying and
            pasting it from the email is easiest.
          </p>
          {#if user && balance > 0}
            <p class="existing-balance">
              You currently have <b>{formatPrice(balance)}</b> in gift balance
              on your account.
            </p>
          {/if}
        {/if}
      </form>
    {:else if result === 'card-added'}
      <div class="result success" role="status">
        <h3>
          <svg class="icon"><use href="/icons.svg#check" /></svg>
          {formatPrice(addedAmount)} was added to your account!
        </h3>
        <p>
          Your total gift balance is now <b>{formatPrice(balance)}</b>. Choose
          how you'd like to use it below.
        </p>
      </div>
    {:else if result === 'card-needs-account' || result === 'needs-account'}
      <div class="result info" role="status">
        {#if result === 'card-needs-account'}
          <h3>Good news! Your gift card is worth {formatPrice(cardAmount)}.</h3>
          <p>
            To save it, create a free account or sign in. We'll remember your
            code and bring you right back here to finish.
          </p>
        {:else}
          <h3>Sign in to check this code</h3>
          <p>
            Membership gift codes can only be checked once you're signed in.
            Create a free account or sign in, and we'll bring you right back
            here to finish.
          </p>
        {/if}
        <div class="auth-buttons">
          <a class="btn big-btn" href={`/register?referrer=${returnTo}`}>
            Create an Account
          </a>
          <a class="btn btn-outline big-btn" href={`/login?referrer=${returnTo}`}>
            I already have an account
          </a>
        </div>
      </div>
    {:else if result === 'membership' && gifted}
      <div class="result success" role="status">
        <h3>
          <svg class="icon"><use href="/icons.svg#gift" /></svg>
          You were gifted: {gifted.product.title}
        </h3>
        <p>
          This membership includes {gifted.shipments} shipment{gifted.shipments > 1 ? 's' : ''}
          per {gifted.product.title.indexOf('Month') > -1 ? 'month' : 'year'}
          with your choice of {gifted.num_rentals} puzzle rentals.
        </p>
        <div class="terms">
          <label>
            <input type="checkbox" bind:checked={termsAcked} />
            <span>
              I agree to the
              <a href="/info/terms-and-conditions" target="_blank">terms and conditions</a>.
            </span>
          </label>
          <label>
            <input type="checkbox" bind:checked={payAcked} />
            <span>
              I understand I will be charged {formatPrice(gifted.bill_price, true)}
              per month if I don't return my puzzle rentals or cancel my
              membership by {formatDate(gifted.bill_date, {
                showTime: false,
                formatStr: 'M/d/yyyy',
              })}.
            </span>
          </label>
        </div>
        {#if notice}
          <div class="notice">
            <Alert
              type={notice.type === 'info' ? 'accent-2' : 'danger'}
              icon={notice.type === 'info' ? 'info' : 'warning'}>
              {notice.text}
            </Alert>
          </div>
        {/if}
        <button
          class="btn big-btn"
          disabled={!termsAcked || !payAcked || starting}
          on:click={() => activateMembership()}>
          {starting ? 'One moment...' : 'Start my membership'}
        </button>
        <p class="hint">
          Next, you'll add a credit card. It's only charged after your gift
          ends.
        </p>
      </div>
    {/if}

    {#if result}
      <button class="btn btn-link start-over" on:click={() => startOver()}>
        {result === 'card-added' ? 'Redeem another code' : 'Use a different code'}
      </button>
    {/if}
  </div>

  {#if result !== 'membership'}
    <div class="step">
      <h2><span class="step-num">2</span> Choose how to use your gift</h2>
      <p>What would you like to do?</p>

      <div class="choices">
        {#each choices as c}
          <button
            class="choice"
            class:selected={choice === c.id}
            aria-expanded={choice === c.id}
            on:click={() => (choice = choice === c.id ? null : c.id)}>
            <span class="radio" />
            {c.label}
            <svg class="chevron"><use href="/icons.svg#keyboard_arrow_down" /></svg>
          </button>
          {#if choice === c.id}
            {#if c.id === 'membership'}
              <div class="details">
                <p>
                  Choose from our Casual, Avid, or Denver Puzzler memberships and
                  rent new puzzles every month.
                </p>
                <ol>
                  <li>Pick a membership and add it to your cart.</li>
                  <li>
                    At checkout, on the <b>Gift Card</b> step, click
                    <b>Apply my gift balance to this order</b>.
                  </li>
                </ol>
                <p class="important">
                  Your gift balance pays for your monthly membership until it runs
                  out. After that, the card on file is charged each month until you
                  cancel.
                </p>
                <a class="btn big-btn" href="/ways-to-rent">See memberships</a>
              </div>
            {:else if c.id === 'pack'}
              <div class="details">
                <p>Rent puzzles one time, with no monthly membership.</p>
                <ol>
                  <li>Add a Puzzle Pack to your cart.</li>
                  <li>
                    Choose your puzzles: up to 4 mid-size puzzles (or up to 3
                    large ones) for a Puzzle Pack, or up to 2 mid-size puzzles
                    (or 1 large one) for a Mini Puzzle Pack. Large puzzles are
                    1,500 pieces or more, and you have 60 days to finish.
                  </li>
                  <li>
                    At checkout, on the <b>Gift Card</b> step, click
                    <b>Apply my gift balance to this order</b>.
                  </li>
                </ol>
                <p class="important">
                  You'll need a credit card saved on file in case of lost or damaged
                  pieces.
                </p>
                <a class="btn big-btn" href="/puzzle-packs">See Puzzle Packs</a>
              </div>
            {:else}
              <div class="details">
                <p>Shop new and gently used puzzles that are yours to keep.</p>
                <ol>
                  <li>Add the puzzles you want to your cart.</li>
                  <li>
                    At checkout, on the <b>Gift Card</b> step, click
                    <b>Apply my gift balance to this order</b>.
                  </li>
                </ol>
                <a class="btn big-btn" href="/shop">Shop puzzles</a>
              </div>
            {/if}
          {/if}
        {/each}
      </div>
    </div>
  {/if}

  <p class="help">
    Need help? Email us at
    <a href="mailto:info@puzzleexchange.com">info@puzzleexchange.com</a>
    or see our <a href="/info/faq">FAQ</a>.
  </p>
</section>
